/**
 * Loads depth tiles (spec 09) for a search window and assembles one cell grid.
 * PNGs are decoded via createImageBitmap + OffscreenCanvas (offline-cached by the
 * SW like chart tiles). Missing tiles stay CELL_UNKNOWN (blocked).
 */
import { lonToTileX, latToTileY, CELL_UNKNOWN, type Bbox } from '@boatnav/core';
import { loadDepthEdition } from './depthMetaStore.js';

export const DEPTH_Z = 13;
/** API path depth tiles are served from (spec 10) — shared with downloadEngine. */
export const DEPTH_TILES_PATH = '/depthtiles';
const TILE = 256;

export interface DepthWindow {
  cells: Uint8Array;
  width: number;
  height: number;
  tx0: number;
  ty0: number;
  z: number;
}

export function autoRouteSupported(): boolean {
  return (
    typeof Worker !== 'undefined' &&
    typeof createImageBitmap === 'function' &&
    typeof OffscreenCanvas !== 'undefined'
  );
}

async function loadTile(tx: number, ty: number, edition: string | null): Promise<Uint8Array | null> {
  try {
    const v = edition ? `?v=${encodeURIComponent(edition)}` : '';
    const res = await fetch(`${DEPTH_TILES_PATH}/${DEPTH_Z}/${tx}/${ty}.png${v}`);
    if (!res.ok) return null;
    const bmp = await createImageBitmap(await res.blob());
    const canvas = new OffscreenCanvas(TILE, TILE);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0);
    const img = ctx.getImageData(0, 0, TILE, TILE);
    const cells = new Uint8Array(TILE * TILE);
    for (let k = 0; k < cells.length; k++) cells[k] = img.data[k * 4]!; // R channel
    return cells;
  } catch {
    return null;
  }
}

export async function loadDepthWindow(bbox: Bbox): Promise<DepthWindow> {
  const edition = await loadDepthEdition();
  const tx0 = lonToTileX(bbox.west, DEPTH_Z);
  const tx1 = lonToTileX(bbox.east, DEPTH_Z);
  const ty0 = latToTileY(bbox.north, DEPTH_Z); // north → smaller y
  const ty1 = latToTileY(bbox.south, DEPTH_Z);
  const cols = tx1 - tx0 + 1;
  const rows = ty1 - ty0 + 1;
  const width = cols * TILE;
  const height = rows * TILE;
  const cells = new Uint8Array(width * height).fill(CELL_UNKNOWN);

  await Promise.all(
    Array.from({ length: cols * rows }, (_, n) => {
      const tx = tx0 + (n % cols);
      const ty = ty0 + Math.floor(n / cols);
      return loadTile(tx, ty, edition).then((tile) => {
        if (!tile) return;
        const ox = (tx - tx0) * TILE;
        const oy = (ty - ty0) * TILE;
        for (let j = 0; j < TILE; j++)
          for (let i = 0; i < TILE; i++) cells[(oy + j) * width + (ox + i)] = tile[j * TILE + i]!;
      });
    }),
  );

  return { cells, width, height, tx0, ty0, z: DEPTH_Z };
}
